"use client";

import { useRouter } from "next/navigation";
import { Heart, Calendar } from "lucide-react";

interface LoginPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  redirectUrl?: string;
  title?: string;
  description?: string;
  type?: "wishlist" | "booking";
  buttonText?: string;
}

export const LoginPromptModal = ({
  isOpen,
  onClose,
  redirectUrl,
  title,
  description,
  type = "wishlist",
  buttonText,
}: LoginPromptModalProps) => {
  const router = useRouter();

  if (!isOpen) return null;

  // Default content based on type
  const defaultTitle =
    type === "booking" ? "Sign In to Book" : "Save to Wishlist";
  const defaultDescription =
    type === "booking"
      ? "Please sign in to your account to proceed with your hotel booking."
      : "Please sign in to your account to save hotels to your wishlist for future reference.";
  const defaultButtonText =
    type === "booking" ? "Continue to Sign In & Book" : "Continue to Sign In";

  // Icon configuration based on type
  const getIcon = () => {
    if (type === "booking") {
      return <Calendar className="w-6 h-6 text-purple-600" />;
    }
    return (
      <svg
        className="w-6 h-6 text-purple-600"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    );
  };

  const getIconBackground = () => {
    return "bg-purple-100";
  };

  const getButtonColor = () => {
    return "bg-purple-600 hover:bg-purple-700";
  };

  const handleContinue = async () => {
    // Close modal immediately
    onClose();

    // Navigate to sign-in with optional redirect URL
    const signInUrl = new URL("/customer/sign-in", window.location.origin);
    if (redirectUrl) {
      signInUrl.searchParams.set("redirect", redirectUrl);
    }

    // Small delay to ensure modal closes before navigation
    setTimeout(() => {
      router.push(signInUrl.pathname + signInUrl.search);
    }, 100);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-lg p-8 max-w-lg mx-4">
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div
              className={`w-12 h-12 ${getIconBackground()} rounded-full flex items-center justify-center`}
            >
              {getIcon()}
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {title || defaultTitle}
          </h2>

          <p className="text-gray-600 mb-6">
            {description || defaultDescription}
          </p>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleContinue}
              className={`flex-1 px-4 py-2 ${getButtonColor()} text-white rounded-lg font-medium transition-colors`}
            >
              {buttonText || defaultButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
